<template>
  <div
    v-if="!isLoading && product.handle"
    class="product-description"
    :class="{ 'is-loading': parentState !== 'ready' }"
  >
    <div
      v-for="(id, index) in filteredAccordionIds"
      :key="index"
      class="product-description__accordion-panel accordion-panel"
    >
      <template v-if="id.id === 'productDetailsFit'">
        <div
          class="
            product-description__product-details
            accordion-panel__product-details
          "
        >
          <h2
            class="product-description__product-details-heading"
            v-html="getTitle(id.id)"
          />

          <div
            v-if="id?.value"
            class="product-description__product-details-copy"
            v-html="removeListItemSpan(decodeHtmlContent(id.value))"
          />

          <div
            v-else-if="parentState !== 'ready'"
            class="product-description__product-details-copy"
          >
            <ul>
              <li
                v-if="product?.whyWeLoveIt"
                v-text="product.whyWeLoveIt"
              />
              <li>
                <span style="animation-delay: 0.1s;" />
                <span style="animation-delay: 0.2s;" />
              </li>
            </ul>
          </div>
        </div>
      </template>
      <template v-else>
        <h2
          class="accordion-panel__accordion-header"
          :aria-expanded="isOpen[index]"
          :aria-controls="id.id"
          @click="toggleAccordion(index)"
        >
          <span v-html="getTitle(id.id)" />
        </h2>

        <div
          :id="id.id"
          class="accordion-panel__accordion-content"
          :aria-expanded="isOpen[index]"
        >
          <template v-if="id.id === 'deliveryReturns' && getUspList?.items?.length">
            <div class="product-description__delivery-returns">
              <ul
                class="product-description__delivery-returns-list"
              >
                <li
                  v-for="(usp, uspIndex) in getUspList.items"
                  :key="uspIndex"
                  class="product-description__delivery-returns-item"
                >
                  <span
                    v-if="usp.icon"
                    class="product-description__delivery-returns-item-icon"
                  >
                    <img
                      alt=""
                      :src="usp.icon"
                    >
                  </span>
                  <span
                    class="product-description__delivery-returns-item-text"
                    v-html="usp.label"
                  />
                </li>
              </ul>
            </div>
          </template>
          <template v-else>
            <div v-html="decodeHtmlContent(id.value)" />
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Component: Product Description
 * ------------------------------------------------------------------------------
 * - Core component for the Product Description accordion component
 *
 * @param {String} parentState - Component state of the parent component.
 * @param {Object} [product] - The product object
 * @param {String} [deliveryReturnsTitle] - delivery & returns title text
 * @param {String} [deliveryReturnsBody] - delivery & returns body text
 * @param {Object} uspList - USP list sourced from metaobjects.
 */

import { decodeHTMLEntities, removeListItemSpanTags } from '~/helpers/strings'
import { buildProductAccordion, getAccordionsTemplate } from '~/helpers/product-accordion'

export default {
  name: 'ProductDescription',

  props: {
    parentState: {
      type: String,
      default: 'loading',
    },
    product: {
      type: Object,
      required: true,
    },
    deliveryReturnsTitle: {
      type: String,
      required: false,
      default: '',
    },
    deliveryReturnsBody: {
      type: String,
      required: false,
      default: '',
    },
    uspList: {
      type: [Boolean, Object],
      default: () => {},
    },
  },

  data() {
    return {
      accordionIds: getAccordionsTemplate(this.deliveryReturnsTitle),
      activeIndex: -1, // Add a data property to store the active index of the accordion.
      isLoading: true, // Stops component rendering if true.
    }
  },

  computed: {

    /**
     * Returns an array of booleans that indicate whether each accordion panel is open or not
     * @returns {Array}
     */
    isOpen() {
      const openArray = []

      for (const [index, value] of this.filteredAccordionIds.entries()) {
        const isOpen = Boolean(index === this.activeIndex)

        openArray.push(isOpen)
      }

      return openArray
    },

    /**
     * Returns an array of strings representing the ids of the panels that have a value
     * @returns {Array}
     */
    filteredAccordionIds() {
      return this.accordionIds.filter(id => id.value !== '')
    },

    /**
    * Get the appropriate USP list for the user's locale.
    * @returns {Array}
    */
    getUspList() {
      if (!this.uspList || !this.uspList?.items) {
        return []
      }

      return this.uspList
    },
  },

  watch: {

    /**
     * Watch product key.
     * - This means the product object has updated
     *   (either initial query or swatch update)
     */
    'product.key': {
      handler: function() {
        this.setAccordionContents()
      },
      immediate: true,
    },
  },

  mounted() {
    this.setAccordionContents()
  },

  created() {
    if (this.checkEmpty()) {
      this.accordionDataLoaded().then(() => {
        this.isLoading = false
      })
    }
    else {
      this.isLoading = false // If all values are empty, set isLoading to false
    }
  },

  methods: {

    /**
     * Wait for filteredAccordionIds to have more than just one item - 'delivery and returns'.
     * - Once this is true this.isLoading will be false so the accordion is rendered.
     */
    async accordionDataLoaded() {
      while (this.filteredAccordionIds.length === 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    },

    /**
     * Define accordion content.
     * - Runs on mount as well as when product object updates.
     */
    setAccordionContents() {
      if (!this.product?.whyWeLoveIt && !this.product?.fusefabric?.whyWeLoveIt) {
        return ''
      }

      this.accordionIds = buildProductAccordion(
        this.product,
        {
          title: this.deliveryReturnsTitle,
          body: this.deliveryReturnsBody,
        },
        this.uspList,
      )
    },

    /**
   * Check to see if at least one of the product properties is not empty.
   * Returns true if at least one of these properties is not empty, false otherwise.
   * Used as part of the created() hook in conjunction with accordionDataLoaded method to set
   * isLoading to false if there are no values.
   * @returns {Boolean}
   */
    checkEmpty() {
      const whyWeLoveIt = this.product?.whyWeLoveIt || ''
      const sizeFit = this.product?.sizeFit || ''
      const fabricCare = this.product?.detailsCare || ''
      const deliveryReturns = this.deliveryReturnsBody || ''

      return whyWeLoveIt || sizeFit || fabricCare || deliveryReturns // Return true if at least one of the values is not empty
    },

    /**
     * Updates the active index based on the clicked accordion panel index
     * @param {Number}
     */
    toggleAccordion(clickedIndex) {
      this.activeIndex = this.activeIndex === clickedIndex ? -1 : clickedIndex
    },

    /**
     * Decodes html entities in a given string and returns the plain text version
     * @param {String} htmlString The string that contains html entities
     * @returns {String} The decoded plain text string
     */
    decodeHtmlContent(htmlString) {
      if (!htmlString) {
        return ''
      }
      return decodeHTMLEntities(htmlString)
    },

    /**
     * Returns string containing list items with any span tags inside it removed
     * @param {String}
     * @returns {String}
     */
    removeListItemSpan(htmlString) {
      return removeListItemSpanTags(htmlString)
    },

    /**
     * Returns the title for a given accordion id
     * @param {String}
     * @returns {String}
     */
    getTitle(id = false) {
      // search for the id object that matches the given id parameter in the accordionIds array
      const idObject = this.accordionIds.find(obj => obj.id === id)
      // if the id object exists, return its value property, otherwise return an empty string
      return idObject ? idObject.label : ''
    },
  },
}
</script>
